import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Layout from "../Layout.tsx";
import Modal from "../Modal.tsx";
import { showModal, hideModal } from "../../data/modal";
import {
  getUsers,
  createUser as createUserAction,
  updateUser as updateUserAction,
  removeUser as removeUserAction,
} from "../../data/users";
import {
  getTenants,
  createTenant as createTenantAction,
  updateTenant as updateTenantAction,
  removeTenant as removeTenantAction,
} from "../../data/tenants";
import {
  getTerminals,
  createTerminal as createTerminalAction,
  updateTerminal as updateTerminalAction,
  removeTerminal as removeTerminalAction,
} from "../../data/terminals";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { User } from "../../types/User";
import { Tenant } from "../../types/Tenant";
import { Terminal } from "../../types/Terminal";

const parseJwt = (token: string | null) => {
  if (!token) { return null; }
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace('-', '+').replace('_', '/');
  return JSON.parse(window.atob(base64));
};

const GenesisAdmin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'users' | 'tenants' | 'terminals'>('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState<{ type: string; id: string; name: string } | null>(null);

  // Get data from Redux store
  const users = useSelector((state: any) => state.users?.data || []);
  const tenants = useSelector((state: any) => state.tenants?.data || []);
  const terminals = useSelector((state: any) => state.terminals?.data || []);
  const usersLoading = useSelector((state: any) => state.users?.loading || false);
  const tenantsLoading = useSelector((state: any) => state.tenants?.loading || false);
  const terminalsLoading = useSelector((state: any) => state.terminals?.loading || false);
  const usersError = useSelector((state: any) => state.users?.error || null);
  const tenantsError = useSelector((state: any) => state.tenants?.error || null);
  const terminalsError = useSelector((state: any) => state.terminals?.error || null);

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate('/login');
      return;
    }

    const user = parseJwt(token);
    if (!user || !user.roles?.includes("ROLE_SUPER_ADMIN")) {
      navigate('/login');
      return;
    }
  }, [navigate]);

  // Fetch data on mount
  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      setError(null);
      try {
        await Promise.all([
          dispatch(getUsers() as any),
          dispatch(getTenants() as any),
          dispatch(getTerminals() as any),
        ]);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, [dispatch]);

  // Refresh data
  const refreshData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        dispatch(getUsers() as any),
        dispatch(getTenants() as any),
        dispatch(getTerminals() as any),
      ]);
    } catch (err) {
      console.error('Error refreshing data:', err);
      setError('Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on search term
  const filteredUsers = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return users.filter((u: User) => 
      u.username?.toLowerCase().includes(term) ||
      u.firstName?.toLowerCase().includes(term) ||
      u.lastName?.toLowerCase().includes(term) ||
      u.email?.toLowerCase().includes(term)
    );
  }, [users, searchTerm]);

  const filteredTenants = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return tenants.filter((t: Tenant) => 
      t.name?.toLowerCase().includes(term)
    );
  }, [tenants, searchTerm]);

  const filteredTerminals = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return terminals.filter((t: Terminal) => 
      t.name?.toLowerCase().includes(term) ||
      t.abbreviation?.toLowerCase().includes(term) ||
      t.city?.toLowerCase().includes(term)
    );
  }, [terminals, searchTerm]);

  // CRUD operations using Redux actions
  const createUser = (userData: Partial<User>) => {
    dispatch(createUserAction(userData, () => dispatch(hideModal())) as any);
  };

  const updateUser = (userData: Partial<User>) => {
    dispatch(updateUserAction(userData) as any);
  };

  const deleteUser = (user: User) => {
    dispatch(removeUserAction(user) as any);
    setDeleteDialogOpen(false);
  };

  const createTenant = (tenantData: Partial<Tenant>) => {
    dispatch(createTenantAction(tenantData, () => dispatch(hideModal())) as any);
  };

  const updateTenant = (tenantData: Partial<Tenant>) => {
    dispatch(updateTenantAction(tenantData) as any);
  };

  const deleteTenant = (tenant: Tenant) => {
    dispatch(removeTenantAction(tenant) as any);
    setDeleteDialogOpen(false);
  };

  const createTerminal = (terminalData: Partial<Terminal>) => {
    dispatch(createTerminalAction(terminalData, () => dispatch(hideModal())) as any);
  };

  const updateTerminal = (terminalData: Partial<Terminal>) => {
    dispatch(updateTerminalAction(terminalData) as any);
  };

  const deleteTerminal = (terminal: Terminal) => {
    dispatch(removeTerminalAction(terminal) as any);
    setDeleteDialogOpen(false);
  };

  // Modal handlers
  const openCreateModal = (type: 'users' | 'tenants' | 'terminals') => {
    dispatch(showModal({
      title: `Create ${type.charAt(0).toUpperCase() + type.slice(1)}`,
      description: `Create a new ${type.slice(0, -1)}`,
      form: `genesis-${type}`,
      formProps: {
        mode: 'create',
        type,
        tenants,
        onSave: (data: any) => {
          if (type === 'users') createUser(data);
          else if (type === 'tenants') createTenant(data);
          else if (type === 'terminals') createTerminal(data);
        },
      },
      size: 'default',
    }) as any);
  };

  const openEditModal = (type: 'users' | 'tenants' | 'terminals', item: any) => {
    dispatch(showModal({
      title: `Edit ${type.charAt(0).toUpperCase() + type.slice(1)}`,
      description: `Edit ${type.slice(0, -1)}`,
      form: `genesis-${type}`,
      formProps: {
        mode: 'edit',
        type,
        item,
        tenants,
        onSave: (data: any) => {
          if (type === 'users') updateUser(data);
          else if (type === 'tenants') updateTenant(data);
          else if (type === 'terminals') updateTerminal(data);
        },
      },
      size: 'default',
    }) as any);
  };

  const openDeleteDialog = (type: string, id: string, name: string) => {
    setDeleteItem({ type, id, name });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (deleteItem) {
      if (deleteItem.type === 'users') {
        const user = users.find((u: User) => u["@id"] === deleteItem.id || u.id === deleteItem.id);
        if (user) deleteUser(user);
      } else if (deleteItem.type === 'tenants') {
        const tenant = tenants.find((t: Tenant) => t["@id"] === deleteItem.id || t.id === deleteItem.id);
        if (tenant) deleteTenant(tenant);
      } else if (deleteItem.type === 'terminals') {
        const terminal = terminals.find((t: Terminal) => t["@id"] === deleteItem.id || t.id === deleteItem.id);
        if (terminal) deleteTerminal(terminal);
      }
    }
  };

  // Render user row
  const renderUserRow = (user: User) => (
    <TableRow key={user["@id"] || user.id}>
      <TableCell>{user.username}</TableCell>
      <TableCell>{user.firstName}</TableCell>
      <TableCell>{user.lastName}</TableCell>
      <TableCell>{user.email}</TableCell>
      <TableCell>
        {user.tenant ? (typeof user.tenant === 'string' ? user.tenant : user.tenant?.name) : ''}
      </TableCell>
      <TableCell align="right">
        <IconButton onClick={() => openEditModal('users', user)}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton onClick={() => openDeleteDialog('users', user["@id"] || user.id || '', user.username || '')}>
          <DeleteIcon fontSize="small" color="error" />
        </IconButton>
      </TableCell>
    </TableRow>
  );

  // Render tenant row
  const renderTenantRow = (tenant: Tenant) => (
    <TableRow key={tenant["@id"] || tenant.id}>
      <TableCell>{tenant.name}</TableCell>
      <TableCell align="center">
        <Chip label={tenant.isActive ? 'Active' : 'Inactive'} color={tenant.isActive ? 'success' : 'default'} />
      </TableCell>
      <TableCell align="right">
        <IconButton onClick={() => openEditModal('tenants', tenant)}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton onClick={() => openDeleteDialog('tenants', tenant["@id"] || tenant.id || '', tenant.name || '')}>
          <DeleteIcon fontSize="small" color="error" />
        </IconButton>
      </TableCell>
    </TableRow>
  );

  // Render terminal row
  const renderTerminalRow = (terminal: Terminal) => (
    <TableRow key={terminal["@id"] || terminal.id}>
      <TableCell>{terminal.abbreviation}</TableCell>
      <TableCell>{terminal.name}</TableCell>
      <TableCell>{terminal.city}</TableCell>
      <TableCell>{terminal.latitude}, {terminal.longitude}</TableCell>
      <TableCell>
        {terminal.tenant ? (
          Array.isArray(terminal.tenant) ?
            terminal.tenant.map(t => typeof t === 'string' ? t : t.name).join(', ') :
            (typeof terminal.tenant === 'string' ? terminal.tenant : terminal.tenant?.name)
        ) : ''}
      </TableCell>
      <TableCell align="right">
        <IconButton onClick={() => openEditModal('terminals', terminal)}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton onClick={() => openDeleteDialog('terminals', terminal["@id"] || terminal.id || '', terminal.name || '')}>
          <DeleteIcon fontSize="small" color="error" />
        </IconButton>
      </TableCell>
    </TableRow>
  );

  // Get data and columns based on active tab
  const getTabContent = () => {
    switch (activeTab) {
      case 'users':
        return {
          data: filteredUsers,
          columns: [
            { key: 'username', label: 'Username' },
            { key: 'firstName', label: 'First Name' },
            { key: 'lastName', label: 'Last Name' },
            { key: 'email', label: 'Email' },
            { key: 'tenant', label: 'Tenant' },
            { key: 'actions', label: 'Actions', align: 'right' as const },
          ],
          renderRow: renderUserRow,
          emptyMessage: 'No users found',
        };
      case 'tenants':
        return {
          data: filteredTenants,
          columns: [
            { key: 'name', label: 'Name' },
            { key: 'isActive', label: 'Status', align: 'center' as const },
            { key: 'actions', label: 'Actions', align: 'right' as const },
          ],
          renderRow: renderTenantRow,
          emptyMessage: 'No tenants found',
        };
      case 'terminals':
        return {
          data: filteredTerminals,
          columns: [
            { key: 'abbreviation', label: 'Abbreviation' },
            { key: 'name', label: 'Name' },
            { key: 'city', label: 'City' },
            { key: 'location', label: 'Location' },
            { key: 'tenant', label: 'Tenant' },
            { key: 'actions', label: 'Actions', align: 'right' as const },
          ],
          renderRow: renderTerminalRow,
          emptyMessage: 'No terminals found',
        };
      default:
        return {
          data: [],
          columns: [],
          renderRow: () => null,
          emptyMessage: '',
        };
    }
  };

  const { data, columns, renderRow, emptyMessage } = getTabContent();

  // Check if any data is loading
  const isLoading = loading || usersLoading || tenantsLoading || terminalsLoading;
  const hasError = error || usersError || tenantsError || terminalsError;

  return (
    <Layout>
      <Modal />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom>
            Genesis Admin
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Button
                  variant={activeTab === 'users' ? 'contained' : 'outlined'}
                  onClick={() => setActiveTab('users')}
                  startIcon={<AddIcon />}
                >
                  Users
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant={activeTab === 'tenants' ? 'contained' : 'outlined'}
                  onClick={() => setActiveTab('tenants')}
                  startIcon={<AddIcon />}
                >
                  Tenants
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant={activeTab === 'terminals' ? 'contained' : 'outlined'}
                  onClick={() => setActiveTab('terminals')}
                  startIcon={<AddIcon />}
                >
                  Terminals
                </Button>
              </Grid>
              <Grid item xs />
              <Grid item>
                <TextField
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setSearchTerm('')} edge="end">
                          <CloseIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => openCreateModal(activeTab)}
                  startIcon={<AddIcon />}
                >
                  Add {activeTab.charAt(0).toUpperCase() + activeTab.slice(1, -1)}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, height: 'calc(100vh - 250px)', overflow: 'auto' }}>
            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography>Loading...</Typography>
              </Box>
            ) : hasError ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography color="error">{hasError}</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell key={column.key} align={column.align || 'left'}>
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.length > 0 ? (
                      data.map((item: any) => renderRow(item))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={columns.length} align="center">
                          {emptyMessage}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete {deleteItem?.type && deleteItem.name ? `"${deleteItem.name}" (${deleteItem.type})` : ''}?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default GenesisAdmin;
